import { useEffect, useRef } from 'react'
import ReportView, { type ReportProps } from './ReportView'
import { renderReportPng } from '../lib/report'

interface Props extends ReportProps {
  /** Called once with the PNG, or the error; the renderer can then be unmounted. */
  onResult: (result: { png: Blob } | { error: string }) => void
}

/** Mounts the report off screen, waits for it to lay out, and turns it into a PNG. */
export default function ReportRenderer({ onResult, ...report }: Props) {
  const el = useRef<HTMLDivElement>(null)
  const cb = useRef(onResult)
  cb.current = onResult
  useEffect(() => {
    let live = true
    const timer = setTimeout(async () => {
      try {
        if (!el.current) return
        const png = await renderReportPng(el.current)
        if (live) cb.current({ png })
      } catch (e) {
        if (live) cb.current({ error: e instanceof Error ? e.message : String(e) })
      }
    }, 400)
    return () => {
      live = false
      clearTimeout(timer)
    }
    // Render once per mount: the parent remounts it to render again.
  }, [])
  return (
    <div className="report-stage" aria-hidden>
      <ReportView ref={el} {...report} />
    </div>
  )
}
